import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import DrawIcon from "@mui/icons-material/Draw";
import {
  SectionPanel,
  StatusPill,
  CodeChip,
  fmtMoney,
  tokens,
} from "../../../shared/clinical-design";
import { NOTE_STATUS, CHARGE_STATUS } from "../../common/constants";
import * as cdci from "../../services/cdciAmdService";
import { FAKE_NOTE_TEMPLATES } from "../../services/fakeDb";

/**
 * CDCI 4.5 — Firma y Cierre (Pasos 6-7 del journey).
 * Checklist de cierre: campos requeridos completos, POS presente, cargos
 * capturados vs nota documentada; acción de firmar. El mecanismo de firma vía
 * API (updateehrnote / atributos signed) está ⏳ por confirmar — pendiente #3.
 */
export default function SignTab({ appointment, note, charges, onSigned }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const isSigned = note?.status === NOTE_STATUS.SIGNED.key;

  // Campos requeridos de la plantilla que siguen vacíos
  const missingRequired = useMemo(() => {
    if (!note) return [];
    const template = FAKE_NOTE_TEMPLATES.find((t) => t.templateName === note.templateName);
    if (!template) return [];
    const missing = [];
    template.pages.forEach((page) =>
      page.fields.forEach((f) => {
        if (f.required && !String(note.fields[f.fieldname] ?? "").trim()) {
          missing.push(`${page.pageName} → ${f.label}`);
        }
      })
    );
    return missing;
  }, [note]);

  const checklist = useMemo(() => {
    const chargesCaptured = charges?.status === CHARGE_STATUS.CAPTURED.key;
    return [
      {
        label: "Encounter note created",
        pass: Boolean(note),
        detail: note ? note.templateName : "Start the note in the Documentation tab",
      },
      {
        label: "Required fields completed",
        pass: Boolean(note) && missingRequired.length === 0,
        detail:
          missingRequired.length > 0
            ? `${missingRequired.length} missing: ${missingRequired.slice(0, 3).join("; ")}${missingRequired.length > 3 ? "…" : ""}`
            : "All required template fields have a value",
      },
      {
        label: "Place of service present",
        pass: Boolean(appointment?.location?.pos),
        detail: appointment?.location ? `POS ${appointment.location.pos} — ${appointment.location.name}` : "",
      },
      {
        label: "Charges captured for this visit",
        pass: chargesCaptured,
        detail: chargesCaptured
          ? `${charges.procedures.length} CPT · ${charges.diagnoses.length} dx`
          : "Capture charges before signing — no visit should close unbilled",
      },
    ];
  }, [note, charges, appointment, missingRequired]);

  const allPass = checklist.every((c) => c.pass);
  const total = (charges?.procedures || []).reduce((acc, p) => acc + (p.fee || 0) * (p.units || 1), 0);

  const handleSign = async () => {
    setSigning(true);
    try {
      const signed = await cdci.signAndClose(appointment.appointmentId);
      onSigned(signed);
      setConfirmOpen(false);
    } finally {
      setSigning(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 760 }}>
      {isSigned && (
        <Box sx={{ bgcolor: tokens.okBg, border: `1px solid ${tokens.ok}`, borderRadius: "6px", px: 2, py: 1.2, mb: 1.5 }}>
          <Typography sx={{ fontSize: 14, color: tokens.ok, fontWeight: 600 }}>
            Encounter signed and closed {note.signedAt ? `on ${new Date(note.signedAt).toLocaleString()}` : ""}.
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink2 }}>
            The note is signed in AdvancedMD and the appointment is marked as completed.
          </Typography>
        </Box>
      )}

      <SectionPanel title="Closure checklist">
        {checklist.map((c) => (
          <Stack key={c.label} direction="row" spacing={1.2} alignItems="flex-start" sx={{ py: 0.7 }}>
            <StatusPill tone={c.pass ? "ok" : "crit"} label={c.pass ? "Pass" : "Blocked"} dense sx={{ mt: 0.2 }} />
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{c.label}</Typography>
              {c.detail && <Typography sx={{ fontSize: 12.5, color: tokens.ink3 }}>{c.detail}</Typography>}
            </Box>
          </Stack>
        ))}
      </SectionPanel>

      {!isSigned && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<DrawIcon />}
            disabled={!allPass}
            onClick={() => setConfirmOpen(true)}
            sx={{ bgcolor: tokens.primary, "&:hover": { bgcolor: tokens.primary700 }, textTransform: "none", fontWeight: 600 }}
          >
            Sign &amp; close encounter
          </Button>
        </Stack>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: tokens.fontUI, fontSize: 16, fontWeight: 600 }}>
          Sign &amp; close — confirm
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: tokens.ink2, mb: 1 }}>
            This signs the note in AdvancedMD and marks the appointment as completed.
            After signing, the note and charges become read-only.
          </Typography>
          <Stack spacing={0.6}>
            <Typography sx={{ fontSize: 13 }}>
              <strong>Note:</strong> {note?.templateName}
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
              <Typography sx={{ fontSize: 13 }}><strong>Charges:</strong></Typography>
              {(charges?.procedures || []).map((p) => <CodeChip key={p.procId} code={p.cpt} />)}
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 13 }}>{fmtMoney(total)}</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none", color: tokens.ink2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={signing}
            onClick={handleSign}
            sx={{ bgcolor: tokens.primary, "&:hover": { bgcolor: tokens.primary700 }, textTransform: "none", fontWeight: 600 }}
          >
            {signing ? "Signing…" : "Confirm & sign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
